class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/*
  Header menu item. With data-hover-open (snippets/header-mega-menu.liquid),
  a mouse also opens the panel on hover; keyboard, touch and pen use the
  caret button, which always toggles it.
  - Hover opens after a short delay, so passing over the menu doesn't flash
    a panel, and closes a moment after the pointer leaves, so it can move
    from the item into the panel.
  - Only a hover-opened panel closes on leaving; one opened with the caret
    stays until Escape, a click outside or focus leaving.
  - Escape closes a hover-opened panel without moving focus.
  - After the caret or Escape closes it, hover doesn't reopen it until the
    pointer leaves and comes back.
  Focus never moves on hover, and aria-expanded follows the panel's state.
*/
class HeaderMenu extends DetailsDisclosure {
  static OPEN_DELAY = 150;
  static CLOSE_DELAY = 250;

  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    if (!this.hasAttribute('data-hover-open')) return;

    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.openedByHover = false;
    this.hoverSuppressed = false;
    this.onEscape = this.onEscape.bind(this);
    this.addEventListener('pointerenter', this.onPointerEnter.bind(this));
    this.addEventListener('pointerleave', this.onPointerLeave.bind(this));
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
  }

  disconnectedCallback() {
    clearTimeout(this.hoverTimer);
    document.removeEventListener('keydown', this.onEscape);
  }

  onPointerEnter(event) {
    if (event.pointerType !== 'mouse' || this.hoverSuppressed) return;
    clearTimeout(this.hoverTimer);
    if (this.mainDetailsToggle.open) return;
    this.hoverTimer = setTimeout(() => this.openByHover(), HeaderMenu.OPEN_DELAY);
  }

  onPointerLeave(event) {
    if (event.pointerType !== 'mouse') return;
    this.hoverSuppressed = false;
    clearTimeout(this.hoverTimer);
    if (!this.openedByHover) return;
    this.hoverTimer = setTimeout(() => this.close(), HeaderMenu.CLOSE_DELAY);
  }

  // Runs before the summary's default toggle.
  onSummaryClick() {
    clearTimeout(this.hoverTimer);
    if (this.mainDetailsToggle.open) {
      // Closing: don't let the hover that's still there reopen it.
      this.hoverSuppressed = true;
    }
    // Opening (or closing) by click: leaving no longer closes it.
    this.openedByHover = false;
  }

  onEscape(event) {
    // With focus inside, global.js closes the panel and returns focus to
    // the caret.
    if (event.key !== 'Escape' || this.contains(document.activeElement)) return;
    this.hoverSuppressed = true;
    this.close();
  }

  openByHover() {
    const otherMenus = [...document.querySelectorAll('header-menu')].filter(
      (menu) => menu !== this && menu.mainDetailsToggle.open
    );
    // Don't take over a panel someone is using with the keyboard.
    if (otherMenus.some((menu) => menu.contains(document.activeElement))) return;
    otherMenus.forEach((menu) => menu.close());

    this.openedByHover = true;
    this.mainDetailsToggle.open = true;
    this.summary.setAttribute('aria-expanded', true);
  }

  close() {
    clearTimeout(this.hoverTimer);
    super.close();
  }

  onToggle() {
    if (this.hasAttribute('data-hover-open')) {
      // Escape only needs listening for while a hover-opened panel is open.
      if (this.mainDetailsToggle.open && this.openedByHover) {
        document.addEventListener('keydown', this.onEscape);
      } else {
        document.removeEventListener('keydown', this.onEscape);
      }
      if (!this.mainDetailsToggle.open) this.openedByHover = false;
    }

    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
